import {
  useQuery
} from "@tanstack/react-query";

import {
  Leaf,
  LoaderCircle,
  Sparkles
} from "lucide-react";

import {
  Link
} from "react-router";

import {
  getIngredients,
  getPublicIngredientImageUrl
} from "../services/catalog";

export function IngredientsPage() {
  const ingredientsQuery =
    useQuery({
      queryKey: [
        "ingredients",
        "storefront"
      ],

      queryFn: () =>
        getIngredients()
    });

  const ingredients =
    ingredientsQuery.data ?? [];

  return (
    <main>
      {/* ================================================== */}
      {/* HERO */}
      {/* ================================================== */}

      <section className="bg-[#edf1e8]">
        <div className="rini-container py-20 text-center sm:py-24 lg:py-28">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#70845b] shadow-sm">
            <Leaf
              size={25}
              strokeWidth={1.5}
            />
          </span>

          <p className="mt-7 text-sm font-semibold uppercase tracking-[0.24em] text-[#70845b]">
            Naturally considered
          </p>

          <h1 className="rini-heading mx-auto mt-4 max-w-4xl text-5xl font-semibold leading-tight sm:text-6xl lg:text-7xl">
            Ingredients with purpose
          </h1>

          <p className="mx-auto mt-7 max-w-2xl text-lg leading-8 text-[#746d65]">
            Discover the botanical ingredients and thoughtful
            choices behind RiNi bodycare.
          </p>
        </div>
      </section>

      {/* ================================================== */}
      {/* INTRODUCTION */}
      {/* ================================================== */}

      <section className="border-b border-[#5e3b2c]/10 bg-[#fffdf8]">
        <div className="rini-container grid gap-8 py-12 md:grid-cols-3">
          <article className="text-center">
            <Sparkles
              size={22}
              className="mx-auto text-[#70845b]"
            />

            <h2 className="mt-4 font-serif text-xl text-[#38251e]">
              Thoughtful selection
            </h2>

            <p className="mx-auto mt-2 max-w-xs text-sm leading-6 text-[#746d65]">
              Ingredients are chosen to complement RiNi's
              handmade bodycare and haircare rituals.
            </p>
          </article>

          <article className="text-center">
            <Leaf
              size={22}
              className="mx-auto text-[#70845b]"
            />

            <h2 className="mt-4 font-serif text-xl text-[#38251e]">
              Botanical inspiration
            </h2>

            <p className="mx-auto mt-2 max-w-xs text-sm leading-6 text-[#746d65]">
              Our ingredient stories are inspired by familiar
              botanicals and traditional care practices.
            </p>
          </article>

          <article className="text-center">
            <span className="mx-auto flex h-6 items-center justify-center text-xl text-[#70845b]">
              ✦
            </span>

            <h2 className="mt-4 font-serif text-xl text-[#38251e]">
              Made with care
            </h2>

            <p className="mx-auto mt-2 max-w-xs text-sm leading-6 text-[#746d65]">
              Each RiNi product is prepared with attention to
              its formulation, texture and everyday use.
            </p>
          </article>
        </div>
      </section>

      {/* ================================================== */}
      {/* INGREDIENT LIBRARY */}
      {/* ================================================== */}

      <section className="rini-section bg-[#fffdf8]">
        <div className="rini-container">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
              Botanical library
            </p>

            <h2 className="rini-heading mt-3 text-4xl font-semibold sm:text-5xl">
              Explore RiNi ingredients
            </h2>

            <p className="mt-4 leading-7 text-[#746d65]">
              Learn more about the botanicals featured across
              the RiNi collection.
            </p>
          </div>

          {ingredientsQuery.isLoading && (
            <div className="flex min-h-96 items-center justify-center">
              <LoaderCircle
                size={36}
                className="animate-spin text-[#70845b]"
              />
            </div>
          )}

          {ingredientsQuery.isError && (
            <div className="my-12 rounded-[2rem] bg-[#f7e6e2] p-10 text-center text-[#713f36]">
              <h3 className="font-serif text-2xl">
                Ingredients could not be loaded
              </h3>

              <p className="mt-2">
                Please refresh the page and try again.
              </p>
            </div>
          )}

          {!ingredientsQuery.isLoading &&
            !ingredientsQuery.isError &&
            ingredients.length > 0 && (
              <div className="mt-12 grid gap-7 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {ingredients.map(
                  (ingredient) => (
                    <article
                      key={ingredient.id}
                      className="group overflow-hidden rounded-[2rem] border border-[#5e3b2c]/10 bg-white shadow-sm"
                    >
                      <div className="aspect-square overflow-hidden bg-[#e4eadc]">
                        {ingredient.imageKey ? (
                          <img
                            src={
                              getPublicIngredientImageUrl(
                                ingredient.id,
                                ingredient.updatedAt
                              )
                            }
                            alt={
                              ingredient.name
                            }
                            loading="lazy"
                            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center">
                            <Leaf
                              size={44}
                              strokeWidth={1.2}
                              className="text-[#70845b]"
                            />
                          </div>
                        )}
                      </div>

                      <div className="p-6">
                        {ingredient.shortTitle && (
                          <p className="text-xs font-semibold uppercase tracking-[0.17em] text-[#a27a4d]">
                            {
                              ingredient.shortTitle
                            }
                          </p>
                        )}

                        <h3 className="mt-2 font-serif text-2xl text-[#38251e]">
                          {
                            ingredient.name
                          }
                        </h3>

                        {ingredient.description && (
                          <p className="mt-3 line-clamp-4 leading-7 text-[#746d65]">
                            {
                              ingredient.description
                            }
                          </p>
                        )}

                        {ingredient.benefits && (
                          <div className="mt-5 border-t border-[#5e3b2c]/10 pt-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#70845b]">
                              Why RiNi uses it
                            </p>

                            <p className="mt-2 text-sm leading-6 text-[#746d65]">
                              {
                                ingredient.benefits
                              }
                            </p>
                          </div>
                        )}
                      </div>
                    </article>
                  )
                )}
              </div>
            )}

          {!ingredientsQuery.isLoading &&
            !ingredientsQuery.isError &&
            ingredients.length === 0 && (
              <div className="py-24 text-center">
                <Leaf
                  size={38}
                  className="mx-auto text-[#70845b]"
                />

                <h3 className="rini-heading mt-5 text-3xl">
                  Ingredient stories are coming soon
                </h3>
              </div>
            )}
        </div>
      </section>

      {/* ================================================== */}
      {/* SHOP CTA */}
      {/* ================================================== */}

      <section className="bg-[#efe3d2]">
        <div className="rini-container py-16 text-center sm:py-20">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
            Discover RiNi
          </p>

          <h2 className="rini-heading mx-auto mt-4 max-w-2xl text-4xl font-semibold sm:text-5xl">
            Explore bodycare made with thoughtful ingredients
          </h2>

          <p className="mx-auto mt-5 max-w-xl leading-7 text-[#746d65]">
            Browse soaps, haircare, lip care and gifting
            collections across the RiNi store.
          </p>

          <Link
            to="/shop"
            className="rini-button-primary mt-8"
          >
            Shop the collection
          </Link>
        </div>
      </section>
    </main>
  );
}
